/**
 * Multi-platform meeting utilities for Zoom, Teams, WebEx, and Google Meet
 */

import type { PlatformT } from '@/pages/web/_constants/types';

export type MeetingPlatform = 'zoom' | 'teams' | 'webex' | 'meet';

// MeetingInfo interface removed since it's not used

export interface OpenMeetingOptions {
  showFallbackConfirm?: boolean;
  fallbackDelay?: number;
  onAppOpened?: () => void;
  onFallback?: () => void;
}

// Platform detection removed since platform parameter is always provided

/**
 * Maps a meeting URL to the platform value the backend reports as
 * meeting_info.platform, so analytics events emitted before the backend
 * responds (e.g. bot_join_failed) group with bot_join / bot_join_timeout.
 * Host-only match; returns null for unrecognised or invalid URLs.
 */
export const platformFromMeetingUrl = (meetingUrl: string): PlatformT | null => {
  let hostname: string;

  try {
    hostname = new URL(meetingUrl).hostname.toLowerCase();
  } catch {
    return null;
  }

  if (
    hostname.includes('zoom.us') ||
    hostname.includes('zoom.com') ||
    hostname.includes('zoomgov.com')
  ) {
    return 'zoom';
  }
  if (
    hostname.includes('teams.microsoft.com') ||
    hostname.includes('teams.microsoft.us') ||
    hostname.includes('teams.live.com')
  ) {
    return 'microsoft_teams';
  }
  if (hostname.includes('meet.google.com')) return 'google_meet';
  if (hostname.includes('webex.com')) return 'webex';

  return null;
};

// Exact domain or a true subdomain — `zoom.us.evil.tld` must not pass as `zoom.us`
const hostMatches = (hostname: string, ...domains: string[]): boolean =>
  domains.some((d) => hostname === d || hostname.endsWith(`.${d}`));

/**
 * Validates if a URL is a valid meeting URL for the specified platform
 */
export const isValidMeetingUrl = (
  url: string,
  platform: MeetingPlatform,
): boolean => {
  try {
    const urlObj = new URL(url);
    // Only https: links — blocks javascript:/data: URLs that carry an allowed hostname
    if (urlObj.protocol !== 'https:') return false;

    const hostname = urlObj.hostname.toLowerCase();

    switch (platform) {
      case 'zoom':
        return (
          hostMatches(hostname, 'zoom.us', 'zoom.com') &&
          (urlObj.pathname.includes('/j/') ||
            urlObj.pathname.includes('/my/') ||
            urlObj.pathname.includes('/s/'))
        );

      case 'teams':
        return (
          hostMatches(hostname, 'teams.microsoft.com', 'teams.live.com') &&
          urlObj.pathname.includes('/l/meetup-join/')
        );

      case 'webex':
        return (
          hostMatches(hostname, 'webex.com') &&
          (urlObj.pathname.includes('/meet/') || urlObj.search.includes('MTID'))
        );

      case 'meet':
        return hostMatches(hostname, 'meet.google.com');

      default:
        return false;
    }
  } catch {
    return false;
  }
};

/**
 * Creates Zoom deeplink from meeting URL
 */
export const createZoomDeeplink = (meetingUrl: string): string => {
  try {
    const url = new URL(meetingUrl);
    let meetingId = '';
    let password = '';

    if (url.pathname.includes('/j/')) {
      meetingId = url.pathname.split('/j/')[1].split('?')[0];
    } else if (url.pathname.includes('/my/')) {
      meetingId = url.pathname.split('/my/')[1].split('?')[0];
    } else if (url.pathname.includes('/s/')) {
      meetingId = url.pathname.split('/s/')[1].split('?')[0];
    }

    const urlParams = new URLSearchParams(url.search);
    password = urlParams.get('pwd') || urlParams.get('password') || '';

    // Encode so a crafted ID or password can't inject extra deeplink params
    let deeplink = `zoommtg://zoom.us/join?confno=${encodeURIComponent(meetingId)}`;
    if (password) {
      deeplink += `&pwd=${encodeURIComponent(password)}`;
    }

    return deeplink;
  } catch (error) {
    console.error('Error creating Zoom deeplink:', error);
    return meetingUrl;
  }
};

/**
 * Creates Microsoft Teams deeplink from meeting URL
 */
export const createTeamsDeeplink = (meetingUrl: string): string => {
  try {
    // Teams deeplink format: msteams://teams.microsoft.com/l/meetup-join/...
    const url = new URL(meetingUrl);

    if (url.pathname.includes('/l/meetup-join/')) {
      // Extract the meeting ID from the path
      const meetingPath = url.pathname + url.search;
      return `msteams://teams.microsoft.com${meetingPath}`;
    }

    // For other Teams URL formats, try to construct the deeplink
    return `msteams://${url.hostname}${url.pathname}${url.search}`;
  } catch (error) {
    console.error('Error creating Teams deeplink:', error);
    return meetingUrl;
  }
};

/**
 * Creates WebEx deeplink from meeting URL
 */
export const createWebexDeeplink = (meetingUrl: string): string => {
  try {
    const url = new URL(meetingUrl);

    // WebEx deeplink format: webex://webex.com/meet/...
    if (url.pathname.includes('/meet/')) {
      const meetingId = url.pathname.split('/meet/')[1].split('/')[0];
      return `webex://webex.com/meet/${meetingId}`;
    }

    // For other WebEx formats
    return `webex://${url.hostname}${url.pathname}${url.search}`;
  } catch (error) {
    console.error('Error creating WebEx deeplink:', error);
    return meetingUrl;
  }
};

/**
 * Creates Google Meet deeplink from meeting URL
 */
export const createMeetDeeplink = (meetingUrl: string): string => {
  try {
    const url = new URL(meetingUrl);

    // Google Meet deeplink format: googlemeet://meet.google.com/...
    return `googlemeet://${url.hostname}${url.pathname}${url.search}`;
  } catch (error) {
    console.error('Error creating Google Meet deeplink:', error);
    return meetingUrl;
  }
};

/**
 * Creates platform-specific deeplink based on the platform
 */
export const createMeetingDeeplink = (
  meetingUrl: string,
  platform: MeetingPlatform,
): string => {
  switch (platform) {
    case 'zoom':
      return createZoomDeeplink(meetingUrl);
    case 'teams':
      return createTeamsDeeplink(meetingUrl);
    case 'webex':
      return createWebexDeeplink(meetingUrl);
    case 'meet':
      return createMeetDeeplink(meetingUrl);
    default:
      return meetingUrl;
  }
};

/**
 * Gets platform-specific app name for user messaging
 */
export const getPlatformAppName = (platform: MeetingPlatform): string => {
  switch (platform) {
    case 'zoom':
      return 'Zoom';
    case 'teams':
      return 'Microsoft Teams';
    case 'webex':
      return 'WebEx';
    case 'meet':
      return 'Google Meet';
    default:
      return 'Meeting app';
  }
};

/**
 * Opens meeting using platform-specific deeplink with fallback
 */
export const openMeeting = (
  meetingUrl: string,
  platform: MeetingPlatform,
  options: OpenMeetingOptions = {},
): void => {
  const {
    showFallbackConfirm = false,
    fallbackDelay = 4000,
    onAppOpened,
    onFallback,
  } = options;

  if (!meetingUrl) {
    console.error('No meeting URL provided');
    return;
  }

  if (!isValidMeetingUrl(meetingUrl, platform)) {
    console.error(`Invalid ${platform} meeting URL:`, meetingUrl);
    return;
  }

  const deeplink = createMeetingDeeplink(meetingUrl, platform);
  const appName = getPlatformAppName(platform);

  try {
    // Try to open the app using the deeplink
    window.location.href = deeplink;
    onAppOpened?.();

    // Provide a manual fallback option after a delay
    if (showFallbackConfirm) {
      setTimeout(() => {
        const shouldFallback = confirm(
          `If ${appName} didn't open, would you like to join in your browser instead?`,
        );
        if (shouldFallback) {
          window.open(meetingUrl, '_blank');
          onFallback?.();
        }
      }, fallbackDelay);
    }
  } catch (error) {
    console.error(`Error opening ${platform} deeplink:`, error);
    // Immediate fallback on error
    window.open(meetingUrl, '_blank');
    onFallback?.();
  }
};

// Note: Auto-detection functions are available but not needed since platform parameter is always provided
